import _extends from "@babel/runtime/helpers/extends";
import React, { useState } from "react";
import Text from "~/components-shared/Text";
import Card from "~/components-shared/Card/Card";
import { useTranslation } from "react-i18next";
import mixpanel from "mixpanel-browser";
import { useHistory } from "react-router-dom";
import { AuthoritiesEnum, CRUDActionEnum, IntegrationTypeEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import Icon from "~/components-shared/Icon";
import Dropdown from "~/components-shared/Dropdown";
import { useDispatch, useSelector } from "react-redux";
import topicActionCreators from "~/store/topics/actions";
import styled, { useTheme } from "styled-components";
import useAbacExt from "~/hooks/abac";
import { Alert, Avatar, Box, Button, Grid, Picture, Spinner } from "~/components-shared";
import lists from "~/lib/lists";
import topicSelectors from "~/store/topics/selectors";
import { TopicActionEnum } from "~/store/topics/types";
import toastsActionCreators from "~/store/toasts/actions";
import integrationSelectors from "~/store/integrations/selectors";
import Markdown from "~/components/Markdown";
const Description = styled(Markdown)`
  flex-grow: 1;
  width: 100%;
  overflow: auto;
`;
const Actions = styled(Box)`
  margin: -10px -10px 0 0;
  float: right;
`;
const Cap = styled.div`
  display: inline-block;
  width: ${_ref => {
  let {
    theme
  } = _ref;
  return 6 * theme.typography.fontStep;
}}rem;
  min-width: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return 6 * theme.typography.fontStep;
}}rem;
  height: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return 6 * theme.typography.fontStep;
}}rem;
  text-align: center;
  line-height: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return 6 * theme.typography.fontStep;
}}rem;
  border-radius: 50%;
  background-color: ${_ref5 => {
  let {
    theme
  } = _ref5;
  return theme.colors.primary.light;
}};
  position: relative;
  font-weight: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return theme.typography.fontWeight.semiBold;
}};
  font-size: ${_ref7 => {
  let {
    theme
  } = _ref7;
  return 3 * theme.typography.fontStep;
}}rem;
  color: ${_ref8 => {
  let {
    theme
  } = _ref8;
  return theme.colors.primary.main;
}};
  box-shadow: 0 0 0 2px ${_ref9 => {
  let {
    theme
  } = _ref9;
  return theme.colors.background.primary;
}};
`;
const Contributions = styled.div`
  background: ${_ref10 => {
  let {
    theme
  } = _ref10;
  return theme.colors.primary.light;
}};
  width: calc(100% + 2.8em);
  padding: 1em 1em 1.5em;
  text-align: center;
  margin: 0 -1.4em;
  border-bottom-left-radius: ${_ref11 => {
  let {
    theme
  } = _ref11;
  return theme.shape.borderRadius.primary;
}};
  border-bottom-right-radius: ${_ref12 => {
  let {
    theme
  } = _ref12;
  return theme.shape.borderRadius.primary;
}};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
const StyledAvatar = styled(Avatar)`
  box-shadow: 0 0 0 2px ${_ref13 => {
  let {
    theme
  } = _ref13;
  return theme.colors.background.primary;
}};
`;
const StyledSource = styled(Picture)`
  background-color: ${_ref14 => {
  let {
    theme
  } = _ref14;
  return theme.colors.background.primary;
}};
  background-size: 80%;
`;
const TopicCard = _ref15 => {
  let {
    children,
    topic,
    small = false,
    ...rest
  } = _ref15;
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const [hoverFollow, setHoverFollow] = useState(false);
  const history = useHistory();
  const {
    userHasOneOf
  } = useAbacExt();
  const dispatch = useDispatch();
  const status = useSelector(topicSelectors.getStatusByTopic(topic.id));
  const sendStatus = useSelector(topicSelectors.getStatusByTopicSend(topic.id));
  const sendError = useSelector(topicSelectors.getErrorByTopicSend(topic.id));
  const followStatus = useSelector(topicSelectors.getStatusByFollow(topic.id));
  const followError = useSelector(topicSelectors.getErrorByFollow(topic.id));
  const error = sendError || followError;
  const summary = topic.weeklySummary?.startsWith("{") ? JSON.parse(topic.weeklySummary)?.summaryShort : topic.weeklySummary?.split(/\.\s+/)[0];
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const handleView = () => {
    history.push(RoutesEnum.TOPIC.replace(":topicId", topic.id));
    mixpanel.track(`Topic Card – View "${topic?.name}"`, {
      topicId: topic.id
    });
  };
  const handleEdit = () => {
    dispatch(topicActionCreators.actionChanged(CRUDActionEnum.UPDATE, topic.id));
    mixpanel.track(`Topic Card – Edit "${topic?.name}"`, {
      topicId: topic.id
    });
  };
  const handleDuplicate = () => {
    dispatch(topicActionCreators.actionChanged(TopicActionEnum.DUPLICATE, topic.id));
    mixpanel.track(`Topic Card – Duplicate "${topic?.name}"`, {
      topicId: topic.id
    });
  };
  const handleDelete = () => {
    dispatch(topicActionCreators.actionChanged(CRUDActionEnum.DELETE, topic.id));
    mixpanel.track(`Topic Card – Delete "${topic?.name}"`, {
      topicId: topic.id
    });
  };
  const handleRetro = () => {
    dispatch(topicActionCreators.actionChanged(TopicActionEnum.RUN_RETRO, topic.id));
    mixpanel.track(`Topic Card – Run Retro "${topic?.name}"`, {
      topicId: topic.id
    });
  };
  const handleFavourite = () => {
    dispatch(topicActionCreators.togglePinStarted(topic.id));
    mixpanel.track(`Topic Card – Favourite "${topic?.name}"`, {
      topicId: topic.id
    });
  };
  const handleFollow = callback => {
    if (topic.isFollowed) {
      mixpanel.track(`Topic Card – Unfollow "${topic?.name}"`, {
        topicId: topic.id
      });
      dispatch(topicActionCreators.unfollowStarted(topic.id, _ref16 => {
        let {
          data
        } = _ref16;
        return data && typeof callback === "function" && callback();
      }));
    } else {
      mixpanel.track(`Topic Card – Follow "${topic?.name}"`, {
        topicId: topic.id
      });
      dispatch(topicActionCreators.followStarted(topic.id, _ref17 => {
        let {
          data
        } = _ref17;
        return data && typeof callback === "function" && callback();
      }));
    }
  };
  const handleShareInSlack = () => {
    dispatch(topicActionCreators.actionChanged(TopicActionEnum.SHARE_IN_SLACK, topic.id));
    mixpanel.track(`Topic Card – Share in Slack "${topic?.name}"`, {
      topicId: topic.id
    });
  };
  const handleSendInSlack = callback => {
    dispatch(topicActionCreators.sendSlackStarted(topic.id, _ref18 => {
      let {
        data
      } = _ref18;
      if (data === topic.id) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("TopicInfoCard.reportSent"),
          timeout: 10000
        }));
        callback();
      }
    }));
    mixpanel.track(`Topic Card – Send in Slack "${topic?.name}"`, {
      topicId: topic.id
    });
  };
  return /*#__PURE__*/React.createElement(Card, _extends({
    s: {
      p: 5,
      pb: 0
    },
    minHeight: "420px",
    align: "left",
    loading: status === StatusStateEnum.LOADING,
    onClick: handleView,
    color: "background",
    shadow: true
  }, rest), /*#__PURE__*/React.createElement(Text, {
    element: "h4",
    s: {
      mb: 4
    },
    display: "block",
    style: {
      width: "100%"
    },
    textOverflow: true
  }, userHasOneOf([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Actions, {
    align: "center"
  }, topic.theme?.id && /*#__PURE__*/React.createElement(Icon, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("TopicCard.followedFromTrend"),
    src: "link",
    display: "block",
    color: topic.isPinned ? theme.colors.warning.main : "inherit",
    style: {
      zIndex: 1
    },
    s: {
      mx: 2
    }
  }), /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    s: {
      p: 2
    },
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": topic.isPinned ? t("TopicCard.unstar") : t("TopicCard.star"),
    onClick: e => {
      e.stopPropagation();
      handleFavourite();
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    src: topic.isPinned ? "starFill" : "star",
    display: "block",
    color: topic.isPinned ? theme.colors.warning.main : "inherit"
  })), /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    handlerProps: {
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      src: "dots-vertical",
      color: theme.colors.text.primary,
      display: "block"
    })
  }, _ref19 => {
    let {
      isOpen,
      onItemClick
    } = _ref19;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, userHasOneOf([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleEdit();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "pencil",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.edit")), userHasOneOf([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleDuplicate();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "copy",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.duplicate")), userHasOneOf([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      textWrap: true,
      onClick: e => {
        e.stopPropagation();
        handleRetro();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "searchHistory",
      color: "primary",
      s: {
        mr: 2
      }
    }), /*#__PURE__*/React.createElement(Text, null, t("TopicCard.runRetro"))), !!slackIntegration && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleSendInSlack(() => {
          onItemClick();
        });
      }
    }, sendStatus === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }) : /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "send",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("TopicInfoCard.sendToSlack")), !!slackIntegration && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleShareInSlack();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "share",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("TopicInfoCard.shareInSlack")), userHasOneOf([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleDelete();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "bin",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete")));
  })), topic.name), /*#__PURE__*/React.createElement(Description, {
    element: "p",
    s: {
      mb: 4
    },
    color: "textSecondary",
    text: summary || "No summary",
    textOverflow: true
  }), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 3,
    template: "auto auto",
    justify: "space-between",
    fullWidth: true,
    s: {
      mb: 4
    }
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    color: "textSecondary",
    display: "block",
    weight: "regular",
    s: {
      mb: 2
    }
  }, "Key Contributors"), /*#__PURE__*/React.createElement(Box, {
    align: "center"
  }, topic.contributors.length ? /*#__PURE__*/React.createElement(React.Fragment, null, topic.contributors.slice(0, 5).map(member => /*#__PURE__*/React.createElement(StyledAvatar, {
    key: member.id,
    size: 6,
    user: member,
    s: {
      mr: -1
    }
  })), topic.contributors.length > 5 && /*#__PURE__*/React.createElement(Cap, null, "+", topic.contributors.length - 5)) : /*#__PURE__*/React.createElement(Text, null, "-"))), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end",
    style: {
      textAlign: "right"
    }
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    color: "textSecondary",
    display: "block",
    s: {
      mb: 2
    }
  }, "Sources"), /*#__PURE__*/React.createElement(Box, {
    align: "center",
    justify: "end"
  }, topic.sources.length ? /*#__PURE__*/React.createElement(React.Fragment, null, topic.sources?.slice(0, 3).map(source => /*#__PURE__*/React.createElement(StyledSource, {
    key: source,
    s: {
      mx: -1
    },
    width: "1.8rem",
    height: "1.8rem",
    border: "rounded",
    backgroundSize: "cover",
    src: lists.common.apps.logos(theme)[source]
  })), topic.sources.length > 3 && /*#__PURE__*/React.createElement(Cap, null, "+", topic.sources.length - 3)) : /*#__PURE__*/React.createElement(Text, null, "-")))), /*#__PURE__*/React.createElement(Contributions, null, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Text, {
    color: theme.colors.primary.main,
    fontSize: 3,
    weight: "semiBold",
    display: "block"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "union",
    s: {
      mr: 1.5,
      mt: -0.5
    },
    color: theme.colors.primary.main
  }), topic.footprintCount, " contributions"), /*#__PURE__*/React.createElement(Button, {
    onClick: e => {
      e.stopPropagation();
      handleFollow();
    },
    onMouseEnter: () => setHoverFollow(true),
    onMouseLeave: () => setHoverFollow(false),
    color: topic.isFollowed ? "translucent" : "primary",
    display: "block",
    fontSize: 3.5,
    loading: followStatus === StatusStateEnum.LOADING,
    s: {
      py: 2,
      pl: 4,
      pr: topic.isFollowed ? 7 : 4,
      mt: 3
    },
    outline: false,
    maxWidth: "150px",
    textAlign: "center",
    fullWidth: true
  }, topic.isFollowed && /*#__PURE__*/React.createElement(Icon, {
    src: "tick",
    s: {
      mr: 2
    }
  }), topic.isFollowed ? hoverFollow ? t("TopicCard.unfollow") : t("TopicCard.following") : t("TopicCard.follow"))));
};
export default TopicCard;